import React from 'react';
import { useCallback, useState } from 'react';

import { LanguageProvider } from '@components/Language';
import { usePostMessageListener } from '@hooks/usePostMessageListener';
import { InitConfigPayload, PostMessageType } from '@spazious/search-sdk-types';

import AppDynamicClient, { AppDynamicClientProps } from './AppDynamicClient.js';
import AppDynamicTheming from './AppDynamicTheming.js';
import AppHelmetProvider, { AppHelmetProviderProps } from './AppHelmetProvider.js';
import AppHotelChainDataProvider from './AppHotelChainDataProvider.js';
import AppHotelChainSlugProvider from './AppHotelChainSlugProvider.js';
import AppStoreProvider, { AppStoreProviderProps } from './AppStoreProvider.js';

export type AppProviderProps = {
  children?: React.ReactNode;
  reduxStore: AppStoreProviderProps['reduxStore'];
  mocks?: AppDynamicClientProps['mocks'];
  basename?: AppHelmetProviderProps['basename'];
};

/**
 * All the providers of the application.
 */
export const AppProvider: React.FC<AppProviderProps> = ({
  reduxStore,
  children,
  mocks,
  basename,
}) => {
  const [hotelChainSlug, setHotelChainSlug] = useState<string>();

  usePostMessageListener<InitConfigPayload>(
    PostMessageType.INIT_CONFIG,
    useCallback((payload) => {
      if (typeof payload?.hotelChainSlug === 'string') {
        setHotelChainSlug(payload.hotelChainSlug);
      }
    }, [])
  );

  return (
    <AppStoreProvider reduxStore={reduxStore}>
      <AppHotelChainSlugProvider hotelChainSlug={hotelChainSlug}>
        <LanguageProvider>
          <AppDynamicClient mocks={mocks}>
            <AppHelmetProvider basename={basename}>
              <AppHotelChainDataProvider hotelChainSlug={hotelChainSlug}>
                <AppDynamicTheming>{children}</AppDynamicTheming>
              </AppHotelChainDataProvider>
            </AppHelmetProvider>
          </AppDynamicClient>
        </LanguageProvider>
      </AppHotelChainSlugProvider>
    </AppStoreProvider>
  );
};

export default AppProvider;
